import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { normaliseBooksResponse, formatDate, CoverArt, getStatus } from "../utils/books";

const LocalApiUrl = "/api";

function Journal() {
  const [books,      setBooks]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [editing,    setEditing]    = useState(null);
  const [noteText,   setNoteText]   = useState('');
  const [saving,     setSaving]     = useState(false);

  useEffect(() => { fetchBooks(); }, []);

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem("userId");
      const res = await axios.get(`${LocalApiUrl}/books/${userId}`);
      setBooks(normaliseBooksResponse(res.data).sort((a, b) => new Date(b.addedOn) - new Date(a.addedOn)));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const saveNote = async (book) => {
    setSaving(true);
    try {
      await axios.post(`/api/books/update`, { id: book._id, note: noteText });
      setBooks(prev => prev.map(b => b._id === book._id ? { ...b, note: noteText } : b));
      setEditing(null);
    } finally { setSaving(false); }
  };

  const withNotes = books.filter(b => b.note?.trim());
  const withoutNotes = books.filter(b => !b.note?.trim() && getStatus(b) === 'finished');

  const filteredWithNotes = withNotes.filter(b =>
    !search || b.title.toLowerCase().includes(search.toLowerCase()) ||
    b.note.toLowerCase().includes(search.toLowerCase()) ||
    b.author?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-ink-base">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">

        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-cream tracking-tight">Reading Journal</h1>
            <p className="text-cream-muted text-sm mt-0.5">
              {loading ? 'Loading…' : `${withNotes.length} book${withNotes.length !== 1 ? 's' : ''} with notes`}
            </p>
          </div>
          <Link to="/addbook" className="sc-btn-ghost text-xs py-1.5 px-4">← Dashboard</Link>
        </div>

        {/* Search */}
        {withNotes.length > 3 && (
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search notes, titles, authors…"
            className="sc-input text-sm mb-6" />
        )}

        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => <div key={i} className="h-28 bg-ink-surface rounded-xl animate-pulse border border-ink-border" />)}
          </div>
        ) : filteredWithNotes.length === 0 && !search ? (
          <div>
            {/* Empty notes state */}
            <div className="py-16 text-center mb-12">
              <p className="text-cream font-semibold mb-1">No notes yet</p>
              <p className="text-cream-muted text-sm max-w-xs mx-auto mb-1">
                Your reading journal lives here. Add notes to books you've read — quotes, reactions, what stayed with you.
              </p>
            </div>

            {/* Books without notes */}
            {withoutNotes.length > 0 && (
              <div>
                <p className="text-cream-muted text-xs font-medium uppercase tracking-wider mb-4">
                  Books without notes — add one?
                </p>
                <div className="space-y-2">
                  {withoutNotes.slice(0, 8).map((book, i) => (
                    <button key={book._id || i}
                      onClick={() => { setEditing(book); setNoteText(''); }}
                      className="w-full flex items-center gap-3 bg-ink-surface border border-ink-border rounded-xl p-4
                                 hover:border-ink-muted transition-colors text-left">
                      <div className="w-8 h-12 rounded overflow-hidden flex-shrink-0">
                        <CoverArt book={book} className="w-full h-full" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-cream text-sm font-medium line-clamp-1">{book.title}</p>
                        <p className="text-cream-muted text-xs">{book.author}</p>
                      </div>
                      <span className="text-cream-muted text-xs flex-shrink-0">Add note →</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredWithNotes.length === 0 && search && (
              <p className="text-cream-muted text-sm py-8 text-center">No results for "{search}"</p>
            )}
            {filteredWithNotes.map((book, i) => (
              <div key={book._id || i} className="bg-ink-surface border border-ink-border rounded-xl overflow-hidden">
                {/* Book header */}
                <div className="flex items-center gap-3 px-5 pt-4 pb-3 border-b border-ink-border">
                  <div className="w-8 h-12 rounded overflow-hidden flex-shrink-0">
                    <CoverArt book={book} className="w-full h-full" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-cream text-sm font-semibold line-clamp-1">{book.title}</p>
                    <p className="text-cream-muted text-xs">{book.author}
                      {book.finishDate && <span className="ml-2">· {formatDate(book.finishDate)}</span>}
                    </p>
                  </div>
                  <button
                    onClick={() => { setEditing(book); setNoteText(book.note || ''); }}
                    className="text-xs text-cream-muted hover:text-cream transition-colors flex-shrink-0">
                    Edit
                  </button>
                </div>

                {/* Note body or editor */}
                {editing?.title === book.title ? (
                  <div className="p-4">
                    <textarea
                      autoFocus
                      className="sc-input min-h-[120px] resize-none text-sm"
                      placeholder="Your thoughts, quotes, what stayed with you…"
                      value={noteText}
                      onChange={e => setNoteText(e.target.value)}
                    />
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => saveNote(book)} disabled={saving}
                        className="sc-btn-gold text-xs py-1.5 px-4 disabled:opacity-50">
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                      <button onClick={() => setEditing(null)} className="sc-btn-ghost text-xs py-1.5 px-4">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="px-5 py-4">
                    <p className="text-cream-dim text-sm leading-relaxed whitespace-pre-wrap">{book.note}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* "Start a note" for books in notes list */}
        {filteredWithNotes.length > 0 && withoutNotes.length > 0 && (
          <div className="mt-10">
            <p className="text-cream-muted text-xs font-medium uppercase tracking-wider mb-4">
              More books without notes
            </p>
            <div className="flex gap-3 overflow-x-auto scrollbar-none pb-2">
              {withoutNotes.slice(0, 6).map((book, i) => (
                <button key={book._id || i}
                  onClick={() => { setEditing(book); setNoteText(''); }}
                  className="flex-shrink-0 w-20 text-left group">
                  <div className="w-20 h-28 rounded-lg overflow-hidden border border-ink-border group-hover:border-ink-muted transition-colors mb-1.5">
                    <CoverArt book={book} className="w-full h-full" />
                  </div>
                  <p className="text-cream text-[10px] font-medium line-clamp-2">{book.title}</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Journal;
